import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { usePayComponents, useSalaryPreview } from '@/composables/payroll/usePayComponents';
import { useBackdateImpact } from '@/composables/payroll/useEmployees';
import { useDebounce } from '@/composables/shared/useDebounce';
import { formatCurrency } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CURRENCIES } from '@/lib/constants';
import { SALARY_REVISION_TYPE_OPTIONS, type PayComponent, type SalaryStructure, type StructureLineInput } from '@/types/payroll';

interface Props {
  /** Prefills the form when editing / cloning an existing structure. */
  initial?: SalaryStructure;
  /** Enables the back-dating impact check against already-issued payslips. */
  employeeId?: string;
  /** True when this employee has no salary on record yet — the revision is the initial one. */
  isFirstRevision?: boolean;
  /** CTC of the revision currently in force, so the editor can show the delta. */
  currentCtc?: number | null;
  onSave: (data: {
    effectiveFrom: string;
    ctcAnnual: number;
    currency: string;
    notes?: string;
    revisionType?: string;
    reason?: string;
    lines: StructureLineInput[];
  }) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

/** Label describing where a component's number comes from. */
function calcHint(c: PayComponent, value: string): string {
  switch (c.calc) {
    case 'FIXED': return 'fixed amount';
    case 'PERCENT_OF_CTC': return `${value || '0'}% of CTC`;
    case 'PERCENT_OF_BASIC': return `${value || '0'}% of Basic`;
    case 'PERCENT_OF_GROSS': return `${value || '0'}% of Gross`;
    case 'BALANCE': return 'absorbs the remainder';
    default: return '';
  }
}

export function SalaryStructureEditor({
  initial, employeeId, isFirstRevision, currentCtc, onSave, onCancel, isSaving,
}: Props) {
  const { components } = usePayComponents();
  const { preview, breakdown, error, isComputing } = useSalaryPreview();

  const [effectiveFrom, setEffectiveFrom] = useState(
    initial?.effectiveFrom?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [ctc, setCtc] = useState(initial ? String(Number(initial.ctcAnnual)) : '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'INR');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [revisionType, setRevisionType] = useState<string>(
    initial?.revisionType ?? (isFirstRevision ? 'INITIAL' : 'ANNUAL_INCREMENT'),
  );
  const [reason, setReason] = useState(initial?.reason ?? '');

  // Debounced so dragging through a date picker does not fire a request per day.
  const debouncedEffectiveFrom = useDebounce(effectiveFrom, 400);
  const { impact } = useBackdateImpact(employeeId ?? '', debouncedEffectiveFrom);

  // selected lines, in display order
  const [lines, setLines] = useState<StructureLineInput[]>(
    initial?.lines?.map((l) => ({
      componentId: l.componentId,
      valueOverride: l.valueOverride === null ? undefined : Number(l.valueOverride),
      position: l.position,
    })) ?? [],
  );

  // Default a brand-new structure to the whole active catalog — that is almost
  // always what is wanted, and unwanted rows are one click to remove.
  useEffect(() => {
    if (!initial && components.length > 0 && lines.length === 0) {
      setLines(components.map((c, i) => ({ componentId: c.id, position: c.position ?? i })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components.length]);

  const byId = useMemo(() => new Map(components.map((c) => [c.id, c])), [components]);
  const available = components.filter((c) => !lines.some((l) => l.componentId === c.id));

  // Recompute off the debounced values so typing a CTC does not fire a request
  // per keystroke.
  const debouncedCtc = useDebounce(ctc, 350);
  const debouncedLines = useDebounce(JSON.stringify(lines), 350);

  useEffect(() => {
    const amount = Number(debouncedCtc);
    if (!amount || amount <= 0 || lines.length === 0) return;
    preview.mutate({ ctcAnnual: amount, lines });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCtc, debouncedLines]);

  const setOverride = (componentId: string, raw: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.componentId === componentId
          ? { ...l, valueOverride: raw === '' ? undefined : Number(raw) }
          : l,
      ),
    );
  };

  const fmt = (n: number) => formatCurrency(n, currency);

  const canSave =
    Number(ctc) > 0 && lines.length > 0 && !!effectiveFrom && !error && !isComputing;

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      {/* ── Inputs ─────────────────────────────────────────────────────── */}
      <div className="space-y-6 lg:col-span-3">
        <Card>
          <CardHeader><CardTitle className="text-base">Structure</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="effectiveFrom">Effective from <span className="text-destructive">*</span></Label>
              <Input id="effectiveFrom" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
              <p className="text-xs text-muted-foreground">A raise is a new structure from a later date — old payslips stay as issued.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ctc">Annual CTC <span className="text-destructive">*</span></Label>
              <DecimalInput id="ctc" value={ctc} onChange={(e) => setCtc(e.target.value)} placeholder="1200000" />
              {Number(ctc) > 0 && (
                <p className="text-xs text-muted-foreground">{fmt(Number(ctc) / 12)} per month</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="revisionType">Revision type</Label>
              <Select value={revisionType} onValueChange={setRevisionType}>
                <SelectTrigger id="revisionType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SALARY_REVISION_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {SALARY_REVISION_TYPE_OPTIONS.find((o) => o.value === revisionType)?.hint}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. FY27 revision cycle" />
              <p className="text-xs text-muted-foreground">Shown in the salary history.</p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal note, not printed on payslips" />
            </div>

            {/* Change against the revision currently in force. */}
            {currentCtc != null && Number(ctc) > 0 && Number(ctc) !== currentCtc && (
              <div className="sm:col-span-2 rounded-md border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Change from the current salary</p>
                <p className="mt-0.5 text-sm">
                  <span className="tabular-nums">{fmt(currentCtc)}</span>
                  <span className="mx-2 text-muted-foreground">→</span>
                  <span className="font-medium tabular-nums">{fmt(Number(ctc))}</span>
                  <span className={`ml-2 font-medium ${Number(ctc) > currentCtc ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {Number(ctc) > currentCtc ? '+' : ''}
                    {(((Number(ctc) - currentCtc) / currentCtc) * 100).toFixed(1)}%
                  </span>
                </p>
              </div>
            )}

            {/* Back-dating does not touch issued payslips, but the user should
                know what the new effective date reaches back over. */}
            {impact?.isBackdated && (
              <div className="sm:col-span-2 flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="text-xs text-amber-700 dark:text-amber-400">
                  <p className="font-medium">
                    This date is before {impact.affectedPayslips.length} already-issued payslip
                    {impact.affectedPayslips.length === 1 ? '' : 's'}.
                  </p>
                  <p className="mt-1">{impact.note}</p>
                  <p className="mt-1 font-mono">
                    {impact.affectedPayslips.map((p) => p.payslipNumber).join(', ')}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Components</CardTitle>
            {available.length > 0 && (
              <Select
                value=""
                onValueChange={(id) => setLines((p) => [...p, { componentId: id, position: p.length }])}
              >
                <SelectTrigger className="w-[220px]">
                  <span className="flex items-center gap-2 text-sm"><Plus className="h-4 w-4" />Add component</span>
                </SelectTrigger>
                <SelectContent>
                  {available.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label} ({c.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {(['EARNING', 'DEDUCTION', 'EMPLOYER_CONTRIBUTION'] as const).map((group) => {
              const groupLines = lines.filter((l) => byId.get(l.componentId)?.type === group);
              if (groupLines.length === 0) return null;
              const title = group === 'EARNING' ? 'Earnings'
                : group === 'DEDUCTION' ? 'Deductions' : 'Employer contributions';
              return (
                <div key={group}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
                  <div className="space-y-2">
                    {groupLines.map((line) => {
                      const c = byId.get(line.componentId);
                      if (!c) return null;
                      const effective = line.valueOverride ?? (c.defaultValue !== null ? Number(c.defaultValue) : undefined);
                      const isOverridden = line.valueOverride !== undefined;
                      return (
                        <div key={line.componentId} className="flex items-center gap-3 rounded-md border p-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{c.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.code} · {calcHint(c, String(effective ?? ''))}
                              {c.isBasic && ' · basic'}
                              {!c.proratable && ' · not prorated'}
                            </p>
                          </div>
                          {c.calc !== 'BALANCE' && (
                            <div className="w-28">
                              <DecimalInput
                                value={line.valueOverride ?? ''}
                                placeholder={c.defaultValue !== null ? String(Number(c.defaultValue)) : ''}
                                onChange={(e) => setOverride(line.componentId, e.target.value)}
                                className={isOverridden ? 'border-primary' : undefined}
                              />
                            </div>
                          )}
                          <Button
                            type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                            onClick={() => setLines((p) => p.filter((l) => l.componentId !== line.componentId))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {lines.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No components selected. Add at least one to compute a breakdown.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Blank uses the catalog default. Type a number to override it for this employee only.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Live breakdown ─────────────────────────────────────────────── */}
      <div className="lg:col-span-2">
        <Card className="lg:sticky lg:top-6">
          <CardHeader><CardTitle className="text-base">Monthly breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}

            {!breakdown && !error && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Enter a CTC to see the breakdown.
              </p>
            )}

            {breakdown && !error && (
              <>
                <Section title="Earnings" lines={breakdown.earnings} fmt={fmt} />
                <Row label="Gross earnings" value={fmt(breakdown.grossEarnings)} bold />

                {breakdown.deductions.length > 0 && (
                  <>
                    <Section title="Deductions" lines={breakdown.deductions} fmt={fmt} negative />
                    <Row label="Total deductions" value={`− ${fmt(breakdown.totalDeductions)}`} bold />
                  </>
                )}

                <div className="rounded-md bg-primary/10 p-3">
                  <Row label="Net pay" value={fmt(breakdown.netPay)} bold />
                </div>

                {breakdown.employerContribs.length > 0 && (
                  <>
                    <Section title="Employer contributions" lines={breakdown.employerContribs} fmt={fmt} />
                    <Row label="Total employer cost" value={fmt(breakdown.grossEarnings + breakdown.employerContributions)} bold />
                  </>
                )}

                {Math.abs(breakdown.ctcVariance) > 0.02 && (
                  <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      {fmt(Math.abs(breakdown.ctcVariance))}/month of the CTC is{' '}
                      {breakdown.ctcVariance > 0 ? 'unallocated' : 'over-allocated'}. Add a balance
                      component to absorb the remainder.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            type="button"
            disabled={!canSave || isSaving}
            onClick={() => onSave({
              effectiveFrom,
              ctcAnnual: Number(ctc),
              currency,
              notes: notes || undefined,
              revisionType,
              reason: reason || undefined,
              lines,
            })}
          >
            {isSaving ? 'Saving…' : 'Save revision'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? 'font-semibold' : ''}`}>
      <span className={bold ? '' : 'text-muted-foreground'}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function Section({
  title, lines, fmt, negative,
}: {
  title: string;
  lines: { code: string; label: string; amount: number }[];
  fmt: (n: number) => string;
  negative?: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {lines.map((l) => (
        <Row key={l.code} label={l.label} value={`${negative ? '− ' : ''}${fmt(l.amount)}`} />
      ))}
    </div>
  );
}
